import React, { useContext, useState } from "react"
import { useTranslation } from "react-i18next"

import Link from "../../basics/Link"
import { router } from "../../../utility/router"
import { PageContext } from "../Page"
import { AllFoldersExcept, OnlyFolders, OnlyItalian, OnlyUk } from "../../utility/Folders"
import { LanguageSelect } from "../../components/LanguageSelect"

import LinkedinIcon from '../../../assets/icons/social/linkedin-white.svg'
import YoutubeIcon from '../../../assets/icons/social/youtube-white.svg'
import FacebookIcon from '../../../assets/icons/social/facebook-white.svg'
import InstagramIcon from '../../../assets/icons/social/instagram-white.svg'
import TiktokIcon from '../../../assets/icons/social/tiktok-white.svg'
import ArrowDown from '../../../assets/icons/footer/arrow-down-footer.svg'
import PropTypes from 'prop-types'

const Footer = ({ type, theme, disclaimer = "" }) => {
  const [t] = useTranslation("layout")
  const context = useContext(PageContext)
  const isNegative = theme === 'dark'

  const MinimalFooter = () => {
    const { folder } = context
    const useAbsoluteUrl = type === 'landing (absolute urls)'

    const complaintsUrl = useAbsoluteUrl ? t('absoluteUrls.complaints') : router('footer.complaints', folder)
    const transparencyUrl = useAbsoluteUrl ? t('absoluteUrls.transparency') : router('footer.transparency', folder)
    const accessibilityUrl = useAbsoluteUrl ? t('absoluteUrls.accessibility') : router('footer.accessibility', folder)
    const whistleblowingUrl = useAbsoluteUrl ? t('absoluteUrls.whistleblowing') : router('footer.whistleblowing', folder)
    const privacyUrl = useAbsoluteUrl ? t('absoluteUrls.privacy') : router('footer.cookie-policy', folder)
    const currentYear = new Date().getFullYear()

    return <>
      {disclaimer !== "" && disclaimer}
      <div className="tiny flex flex-wrap whitespace-nowrap pt-3 mb-[10px]">
        <Link to={privacyUrl} className="py-0.5 mr-2.5">{t('footer.cookie-policy')}</Link>
        <Link to={transparencyUrl} className="py-0.5 mr-2.5">{t('footer.transparency')}</Link>
        <Link to={complaintsUrl} className="py-0.5 mr-2.5">{t('footer.complaints')}</Link>
        <Link blank to={router('footer.status-page', context.folder, true)} className="py-0.5 mr-2.5">{t('footer.statusPage')}</Link>
        <Link to={accessibilityUrl} className="py-0.5 mr-2.5">{t('footer.accessibility')}</Link>
        <Link to={whistleblowingUrl} className="py-0.5 mr-2.5">{t('footer.whistleblowing')}</Link>
        <Link blank to={router('footer.ethicalCode', folder, true)} className="py-0.5 mr-2.5">{t('footer.ethicalCode')}</Link>

        <OnlyItalian>
          <Link to={router('footer.reportSustainability', context.folder)} className="py-0.5 mr-2.5">{t('footer.reportSustainability')}</Link>
          <Link to={router('footer.massimaliCommerciali', context.folder)} className="py-0.5 mr-2.5">{t('footer.massimaliCommerciali')}</Link>
          <Link to={router('footer.informativaCommerciale', context.folder)} className="py-0.5 mr-2.5">{t('footer.informativaCommerciale')}</Link>
        </OnlyItalian>
      </div>
      <div className="tiny pb-3 pt-[14px] border-t-1 border-midnight-20">
        <OnlyUk>
          <span className="font-bold">Advertising message for promotional purposes.</span><br />
          * Fabrick's product and service offerings may vary by country. AIS and PIS solutions are not available in the UK. Fabrick provides these solutions only in Italy, Spain and France. If your business has a registered office in Italy, Spain or France and you would like more information, you can <Link className="underline underline-offset-2" to={router('sales', context.folder, false)}>contact us here</Link>.<br /><br />
        </OnlyUk>
        <AllFoldersExcept folders={['en-gb']}>
          {t('footer.disclaimerAd')}<br />
        </AllFoldersExcept>
        © 2017 - {currentYear} Fabrick S.p.A. - {t('footer.disclaimerLegal')}
      </div>
    </>
  }
  Footer.propTypes = {
    type: PropTypes.string,
    theme: PropTypes.string,
    disclaimer: PropTypes.any,
  }

  // SOLO su mobile, diventa un accordion
  const AccordionOnMobile = ({ className = "", title, children }) => {
    const [isOpen, setOpen] = useState(false)

    return <div className={`${className}`}>
      {/* Mobile (Accordion) */}
      <div className="md:hidden relative">
        <div className="text-xs mb-2 select-none">
          {/* Parte cliccabile (più grande del testo) */}
          <div className="absolute h-5 -top-1 w-full cursor-pointer" onClick={() => setOpen(!isOpen)}></div>
          {/* Testo e icona */}
          <div className="flex items-center justify-between">
            <div>{title}</div>
            <div className={`transition-all pointer-events-none ${isOpen ? "rotate-180" : "rotate-0"}`}><ArrowDown className="stroke-white" /></div>
          </div>
        </div>
        {isOpen && children}
      </div>

      {/* Desktop */}
      <div className="hidden md:block">
        <h5 className="mb-2 !text-blue-40">{title}</h5>
        {children}
      </div>
    </div>
  }

  const Products = ({ className = "" }) => {
    return <AccordionOnMobile className={className} title={t('header.products')}>
      <div className="text-body mb-1"><strong>{t('header.productsMenu.psd2Services.title')}</strong></div>
      <Link to={router('products.psd2Services.aisp', context.folder)} className="block text-body mb-1">{t('header.productsMenu.psd2Services.aisp')}</Link>
      <Link to={router('products.psd2Services.pisp', context.folder)} className="block text-body mb-1">{t('header.productsMenu.psd2Services.pisp')}</Link>

      <OnlyFolders folders={["it-it"]}>
        <Link to={router('products.psd2Services.fast-scoring', context.folder)} className="block text-body mb-1">{t('header.productsMenu.psd2Services.creditRating')}</Link>
      </OnlyFolders>

      <OnlyFolders folders={["it-it", "fr-fr"]}>
        <Link to={router('products.psd2Services.personalFinance', context.folder)} className="block text-body mb-1">{t('header.productsMenu.psd2Services.personalFinance')}</Link>
      </OnlyFolders>

      <OnlyFolders folders={["it-it"]}>
        <Link to={router('products.psd2Services.bankingAsService', context.folder)} className="block text-body mb-1">{t('header.productsMenu.psd2Services.bankingAsService')}</Link>
      </OnlyFolders>

      <Link to={router('products.psd2Services.bankCoverage', context.folder)} className="block text-body mb-3">{t('header.productsMenu.psd2Services.bankCoverage')}</Link>

      <div className="text-body mb-1"><strong>{t('header.productsMenu.endToEndSolutions.title')}</strong></div>
      <Link to={router('products.endToEndSolutions.financeOrchestration', context.folder)} className="block text-body mb-1">{t('header.productsMenu.endToEndSolutions.financeOrchestration')}</Link>
      <OnlyFolders folders={["it-it"]}>
        <Link to={router('products.endToEndSolutions.sepa', context.folder)} className="block text-body mb-1">{t('header.productsMenu.endToEndSolutions.sepa')}</Link>
      </OnlyFolders>
      <Link to={router('products.endToEndSolutions.paymentGate', context.folder)} className="block text-body mb-1">{t('header.productsMenu.endToEndSolutions.paymentGate')}</Link>
      <Link to={router('products.endToEndSolutions.riskAnalysis', context.folder)} className="block text-body mb-1">{t('header.productsMenu.endToEndSolutions.riskAnalysis')}</Link>
      <Link to={router('products.endToEndSolutions.guaranteedPayments', context.folder)} className="block text-body mb-1">{t('header.productsMenu.endToEndSolutions.guaranteedPayments')}</Link>
      <Link to={router('products.endToEndSolutions.facilitator', context.folder)} className="block text-body mb-1">{t('header.productsMenu.endToEndSolutions.facilitator')}</Link>
      <AllFoldersExcept folders={["it-it", "en-gb"]}>
        <div className="mb-2"></div>
      </AllFoldersExcept>
      <OnlyFolders folders={["it-it", "en-gb"]}>
        <Link to={router('products.endToEndSolutions.financialSplitPayment', context.folder)} className="block text-body mb-3">{t('header.productsMenu.endToEndSolutions.financialSplitPayment')}</Link>
      </OnlyFolders>

      <AllFoldersExcept folders={["es-es"]}>
        <div className="text-body mb-1"><strong>{t('header.productsMenu.bankingServices.title')}</strong></div>
        <OnlyFolders folders={["it-it"]}>
          <Link to={router('products.bankingServices.businessFinance', context.folder)} className="block text-body mb-1">{t('header.productsMenu.bankingServices.businessFinance')}</Link>
          <Link to={router('products.bankingServices.smeBanking', context.folder)} className="block text-body mb-1">{t('header.productsMenu.bankingServices.smeBanking')}</Link>
        </OnlyFolders>
        <Link to={router('products.bankingServices.lendingPlace', context.folder)} className="block text-body mb-1">{t('header.productsMenu.bankingServices.lendingPlace')}</Link>
        <OnlyFolders folders={["it-it"]}>
          <Link to={router('products.bankingServices.onboarding', context.folder)} className="block text-body mb-1">{t('header.productsMenu.bankingServices.onboarding')}</Link>
          <Link to={router('products.bankingServices.checkIban', context.folder)} className="block text-body mb-1">{t('header.productsMenu.bankingServices.checkIban')}</Link>
        </OnlyFolders>
        <Link to={router('products.bankingServices.smartBanking', context.folder)} className="block text-body mb-1">{t('header.productsMenu.bankingServices.smartBanking')}</Link>
      </AllFoldersExcept>
    </AccordionOnMobile>
  }

  const StartupFintech = ({ className = "" }) => {
    return <AccordionOnMobile className={className} title={t('header.startupSolutions')}>
      <Link to={router('developers.startup-solutions', context.folder)} className="block text-body mb-1">{t('header.fabrickXStartups')}</Link>
    </AccordionOnMobile>
  }

  const Industry = ({ className = "" }) => {
    return <AccordionOnMobile className={className} title={t('header.industry')}>
      <Link to={router('customer-stories.fashion', context.folder)} className="block text-body mb-1">{t('header.customerStories.fashion')}</Link>
      <Link to={router('customer-stories.fintech', context.folder)} className="block text-body mb-1">{t('header.customerStories.fintech')}</Link>
      <Link to={router('customer-stories.horeca', context.folder)} className="block text-body mb-1">{t('header.customerStories.horeca')}</Link>
      <Link to={router('customer-stories.insurance', context.folder)} className="block text-body mb-1">{t('header.customerStories.insurance')}</Link>
      <Link to={router('customer-stories.banking', context.folder)} className="block text-body mb-1">{t('header.customerStories.banking')}</Link>
      <OnlyFolders folders={["it-it", "en-gb"]}>
        <Link to={router('customer-stories.automotive', context.folder)} className="block text-body mb-1">{t('header.customerStories.automotive')}</Link>
      </OnlyFolders>
      <Link to={router('customer-stories.customer-stories', context.folder)} className="block text-body mb-1">{t('header.customerStories.customerStories')}</Link>
    </AccordionOnMobile>
  }

  const Insights = ({ className = "" }) => {
    return <AccordionOnMobile className={className} title={t('header.insights')}>
      <Link to={router('insights.articles', context.folder)} className="block text-body mb-1">{t('header.insightsMenu.articles')}</Link>
      {/* <Link to={router('insights.podcast', context.folder)} className="block text-body mb-1">{t('header.insightsMenu.podcast')}</Link> */}
      <Link to={router('insights.whitepaper', context.folder)} className="block text-body mb-1">{t('header.insightsMenu.whitepaper')}</Link>
      <Link to={router('insights.newsletter', context.folder)} className="block text-body mb-1">{t('header.insightsMenu.newsletter')}</Link>

      {/* <Link to={router('insights.video', context.folder)} className="block text-body mb-1">{t('header.insightsMenu.video')}</Link> */}
      {/* <Link to={router('insights.webinar', context.folder)} className="block text-body mb-3">{t('header.insightsMenu.webinar')}</Link> */}
    </AccordionOnMobile>
  }

  const Developers = ({ className = "" }) => {
    return <AccordionOnMobile className={className} title={t('header.developers')}>
      <Link to={router('developers.api', context.folder)} className="block text-body mb-1">{t('header.devsMenu.api')}</Link>
      <Link to={router('developers.fabrick-producers', context.folder)} className="block text-body mb-1">{t('header.devsMenu.producers')}</Link>
      <Link to={router('developers.tpp-portal', context.folder)} className="block text-body mb-1">{t('header.devsMenu.tpp')}</Link>
      <a className="block text-body text-link-unstyled mb-1" href="https://www.platfr.io/#/platfr/login" target="_blank" rel='noopener'>{t('header.devsMenu.consumerPortal')}</a>
      {/* <Link to={'https://www.platfr.io/#/platfr/login'} blank className="block text-body mb-1"></Link> */}
      {/* <Link to={router('developers.assistenza-tecnica', context.folder)} className="block text-body mb-1">{t('header.devsMenu.support')}</Link> */}
    </AccordionOnMobile>
  }

  const Support = ({ className = "" }) => {
    return <AccordionOnMobile className={className} title={t('header.support')}>
      {/* <Link to='https://developers.fabrick.com' blank={true} className="block text-body mb-1">{t('header.supportMenu.faq')}<ExternalLinkIcon className="inline-block ml-1 relative bottom-[2px]" /></Link> */}
      <Link to={router('support.customer-support', context.folder)} className="block text-body mb-1">{t('header.supportMenu.support')}</Link>
    </AccordionOnMobile>
  }

  const AboutFabrick = ({ className = "" }) => {
    return <AccordionOnMobile className={className} title={t('header.aboutFabrick')}>
      <Link to={router('aboutFabrick.mission', context.folder)} className="block text-body mb-1">{t('header.aboutFabrickMenu.mission')}</Link>
      <Link to={router('aboutFabrick.platform', context.folder)} className="block text-body mb-1">{t('header.aboutFabrickMenu.platform')}</Link>
      <Link to={router('aboutFabrick.ecosystem', context.folder)} className="block text-body mb-1">{t('header.aboutFabrickMenu.ecosystem')}</Link>
      <Link to={router('aboutFabrick.team', context.folder)} className="block text-body mb-1">{t('header.aboutFabrickMenu.team')}</Link>
      <Link to={router('aboutFabrick.history', context.folder)} className="block text-body mb-1">{t('header.aboutFabrickMenu.history')}</Link>
    </AccordionOnMobile>
  }

  const JoinUs = ({ className = "" }) => {
    return <AccordionOnMobile className={className} title={t('footer.joinUs')}>
      <Link to={router('fabrickPeople.joinUs', context.folder)} className="block text-body mb-1">{t('footer.openPositions')}</Link>
      <Link to={router('fabrickPeople.whyFabrick', context.folder)} className="block text-body mb-1">{t('footer.whyFabrick')}</Link>
      <Link to={router('fabrickPeople.ourTeam', context.folder)} className="block text-body mb-1">{t('footer.ourTeam')}</Link>
    </AccordionOnMobile>
  }

  const MediaHub = ({ className = "" }) => {
    return <AccordionOnMobile className={className} title={t('header.mediaHub')}>
      <Link to={router('mediaHub.pressReleases', context.folder)} className="block text-body mb-1">{t('header.pressReleases')}</Link>
      <Link to={router('mediaHub.pressReview', context.folder)} className="block text-body mb-1">{t('header.pressReview')}</Link>
      <Link to={router('mediaHub.events', context.folder)} className="block text-body mb-1">{t('header.events')}</Link>
      <Link to={router('mediaHub.mediaKit', context.folder)} className="block text-body mb-1">{t('header.mediaKit')}</Link>
    </AccordionOnMobile>
  }

  return type !== "hidden" && <footer className={`block px-container-mobile md:px-container-desktop bg-midnight-10 ${isNegative ? 'text-negative' : ''}`}>
    {type === 'full' && <>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 pt-10 mb-7 gap-0 md:gap-3 lg:gap-6">
        <div className="mb-5">
          <div className="text-xs mb-2">{t("footer.getStarted")}</div>
          <Link button negative={isNegative} to={router('contacts', context.folder)}>{t("footer.contactUsButton")}</Link>

          <div className="text-xs mb-2 mt-5">{t("footer.loginDashboard")}</div>
          <Link button negative={isNegative} to={router('login', context.folder)}>{t("footer.loginButton")}</Link>

          <div className="text-xs mt-5 mb-2">{t("footer.country")}</div>
          <LanguageSelect wide negative />

          <div className="text-xs mt-5 mb-2">{t("footer.followSocial")}</div>
          <div className="flex">
            <Link to='https://www.linkedin.com/company/fabrickfinance/' blank={true}>
              <LinkedinIcon />
            </Link>
            {/* <Link to={router('social.spotify', context.folder)} blank={true}>
              <SpotifyIcon className="ml-2" />
            </Link> */}
            <Link to='https://www.youtube.com/@fabrickplatform' blank={true}>
              <YoutubeIcon className="ml-2" />
            </Link>
            <Link to='https://www.facebook.com/fabrickplatform' blank={true}>
              <FacebookIcon className="ml-2" />
            </Link>
            <Link to='https://www.instagram.com/fabrickplatform' blank={true}>
              <InstagramIcon className="ml-2" />
            </Link>
            <Link to='https://www.tiktok.com/@fabrick' blank={true}>
              <TiktokIcon className="ml-2" />
            </Link>
          </div>
        </div>

        <div>
          <Products />
          <OnlyFolders folders={["en-gb"]}>
            <StartupFintech className="mt-3 md:mt-5" />
          </OnlyFolders>
          <Industry className="hidden md:block lg:hidden mt-3 md:mt-5" />
        </div>

        <div>
          <Industry className="md:hidden lg:block mt-1 md:mt-0" />
          <Insights className="mt-3 md:mt-0 lg:mt-5" />
          <Developers className="mt-3 md:mt-5" />
          <OnlyFolders folders={["it-it"]}>
            <StartupFintech className="mt-3 md:mt-5" />
          </OnlyFolders>
          <Support className="lg:hidden mt-3 md:mt-5" />
          <AboutFabrick className="lg:hidden mt-3 md:mt-5" />
          <JoinUs className="lg:hidden mt-3 md:mt-5" />
          <MediaHub className="lg:hidden mt-3 md:mt-5" />
        </div>

        <div>
          <Support className="hidden lg:block" />
          <AboutFabrick className="hidden lg:block mt-3 md:mt-5" />
          <JoinUs className="hidden lg:block mt-3 md:mt-5" />
          <MediaHub className="hidden lg:block mt-3 md:mt-5" />
        </div>
      </div>
    </>}

    <MinimalFooter />
  </footer>
}

export default Footer